'use client';

import { FAQ } from '@/components/FAQ';
import Link from 'next/link';
import Balancer from 'react-wrap-balancer';

export interface LoadCalculatorFAQsProps {
	testId?: string;
}

const LoadCalculatorFAQs = (props: LoadCalculatorFAQsProps) => {
	const { testId = 'LoadCalculatorFAQs' } = props;

	return (
		<FAQ testId={testId}>
			<FAQ.Title>FAQ</FAQ.Title>
			<FAQ.Block>
				<FAQ.Item>
					<FAQ.Question panelId="1">
						What is an HVAC Sizing Calculator?
					</FAQ.Question>
					<FAQ.Answer panelId="1">
						<p>
							Our HVAC Sizing Calculator (also called an AC sizing calculator,
							HVAC tonnage calculator, or HVAC load calculator) helps you
							determine the ideal size of an air conditioning or heating system
							for your home based on its square footage, location, and year
							built. Proper HVAC sizing ensures your system can efficiently cool
							or heat your home without wasting energy or leaving you
							uncomfortable.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="2">
						What is air conditioner tonnage?
					</FAQ.Question>
					<FAQ.Answer panelId="2">
						<p>
							“Tonnage” refers to the cooling capacity of an air conditioner,
							not its weight. One ton of cooling equals 12,000 BTUs (British
							Thermal Units) per hour. For example, a 3.5-ton AC unit can remove
							42,000 BTUs of heat per hour from your home.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="3">
						How do I determine the right size air conditioner or furnace?
					</FAQ.Question>
					<FAQ.Answer panelId="3">
						<p>
							To determine the right size HVAC system for your home, use our
							HVAC Sizing Calculator. Enter your home&apos;s square footage,
							year built, and zip code to get an estimate. If your home has
							special considerations, such as requiring multiple units, multiple
							floors, or high ceilings, speak with one of our technical advisors
							for a more detailed assessment.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="4">
						What does BTU mean for air conditioners?
					</FAQ.Question>
					<FAQ.Answer panelId="4">
						<p>
							BTU stands for British Thermal Unit, a measure of heat energy. To
							estimate the BTUs needed to cool or heat your space, multiply the
							square footage of the home or space by 20. For example, a 500 sq
							ft space needs about 10,000 BTUs (500 sq ft &times; 20 BTU/sq ft).
							This can change depending on your home&apos;s unique
							characteristics. Our technical advisors can assist you in making
							the best decision for your home&apos;s needs.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="5">
						How many BTUs per square foot?
					</FAQ.Question>
					<FAQ.Answer panelId="5">
						<p>
							Generally, you need 20 BTUs for every square foot of space in your
							home. Here&apos;s a guide for BTU choice based on home square
							footage:
						</p>
						<br />
						<ul className="ml-10 list-outside list-disc">
							<Balancer>
								<li>600 &ndash; 1,000 sq ft: 18,000 BTU (1.5 tons)</li>
								<li>1,000 &ndash; 1,300 sq ft: 24,000 BTU (2 tons)</li>
								<li>1,300 &ndash; 1,600 sq ft: 30,000 BTU (2.5 tons)</li>
								<li>1,600 &ndash; 1,900 sq ft: 36,000 BTU (3 tons)</li>
								<li>1,900 &ndash; 2,200 sq ft: 42,000 BTU (3.5 tons)</li>
								<li>2,200 &ndash; 2,600 sq ft: 48,000 BTU (4 tons)</li>
								<li>2,600 &ndash; 3,200 sq ft: 60,000 BTU (5 tons)</li>
							</Balancer>
						</ul>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="6">
						How can I find the BTU rating of my air conditioner?
					</FAQ.Question>
					<FAQ.Answer panelId="6">
						<p>
							Check the model number on your air conditioner&apos;s outdoor
							unit. It often includes a two-digit number representing the BTUs
							in thousands. For instance, &lsqu;24&rsqu; in the model number
							indicates a 24,000 BTU capacity, or 2 tons (1 ton = 12,000 BTUs).
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="7">
						Why is the right AC or furnace size important?
					</FAQ.Question>
					<FAQ.Answer panelId="7">
						<p>
							Choosing the correct size HVAC system is crucial for efficiency
							and comfort. An oversized unit can cycle on and off too quickly,
							failing to dehumidify your home, while an undersized unit may run
							continuously without adequately cooling or heating your space,
							both leading to higher energy costs and reduced comfort. If the
							wrong size system is installed, you&apos;ll need to replace it
							with the appropriate size unit.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="8">
						What factors affect the size of AC I need?
					</FAQ.Question>
					<FAQ.Answer panelId="8">
						<p className="mb-7">
							Bigger spaces, high ceilings, larger or additional windows, hotter
							or cooler climates, insulation quality, and other factors can all
							affect HVAC sizing. Feeling unsure about the next steps for your
							HVAC replacement?{' '}
							<Link
								href="/replace/virtual/"
								className="text-sky-700 hover:text-sky-800 hover:underline"
							>
								Replace your HVAC system with HVAC.com
							</Link>
							. Our team of technical advisors will craft a custom solution for
							your home, ensuring you get trusted help with fair, honest
							pricing.
						</p>
						<ul className="ml-10 list-outside list-disc">
							<li>
								<strong>Larger spaces</strong> require more capacity to maintain
								comfortable temperatures.
							</li>
							<li>
								<strong>High ceilings</strong> mean more air volume to cool or
								heat, generally requiring a 10% to 25% increase in capacity.
							</li>
							<li>
								<strong>Windows:</strong> Large or poorly insulated windows that
								get a lot of sun can increase your home&apos;s heat gain,
								requiring a more powerful system.
							</li>
							<li>
								<strong>House orientation:</strong> Homes facing south usually
								need more cooling due to increased sunlight exposure compared to
								north-facing homes.
							</li>
							<li>
								<strong>Climate variability:</strong> Longer or more intense
								heat waves increase cooling needs. Similarly, colder climates
								raise heating needs.
							</li>
							<li>
								<strong>Number of occupants:</strong> More people generate
								additional body heat, increasing cooling needs. Add
								approximately 600 BTUs of cooling capacity per person.
							</li>
							<li>
								<strong>Insulation quality:</strong> Proper insulation helps
								maintain your home&apos;s internal temperatures by slowing down
								heat gain in summer and heat loss in winter, allowing for
								smaller, more energy-efficient units.
							</li>
							<li>
								<strong>Air leaks:</strong> Air leaks through uninsulated doors,
								windows, and ductwork can cause your system to work harder,
								requiring a larger unit.
							</li>
							<li>
								<strong>Kitchen appliances:</strong> Ovens and stoves generate
								significant heat. If your system covers kitchen areas, consider
								this when choosing your unit size.
							</li>
						</ul>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="9">
						What to do if my AC doesn&apos;t cool my home properly?
					</FAQ.Question>
					<FAQ.Answer panelId="9">
						<p>
							If your air conditioner doesn&apos;t cool your home effectively,
							it might be incorrectly sized, or there may be issues with your
							home&apos;s insulation or air leaks. It&apos;s a good idea to
							contact a professional HVAC technician who can perform a detailed
							load calculation and check for other factors like ductwork
							condition or refrigerant levels.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="10">
						How do I calculate furnace size?
					</FAQ.Question>
					<FAQ.Answer panelId="10">
						<p>
							To calculate furnace size, consider the BTU requirements and the
							furnace&apos;s efficiency. For example, an 80,000 BTU furnace with
							80% efficiency provides 64,000 BTUs of usable heat, while a 96%
							efficient furnace provides 76,800 BTUs. Typically, if a house
							needs 4 tons of cooling (48,000 BTUs), it should also need 4 tons
							of heating, though this can vary with climate. Consult our
							technical advisors for precise calculations.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="11">
						What is a Manual J load calculation?
					</FAQ.Question>
					<FAQ.Answer panelId="11">
						<p>
							A Manual J load calculation, created by the Air Conditioning
							Contractors of America (ACCA), is a detailed method for sizing an
							HVAC unit. It considers factors like climate, house size, windows,
							insulation, and occupancy to ensure your HVAC system is perfectly
							tailored to your home&apos;s needs.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="12">
						How does maintenance affect my HVAC system?
					</FAQ.Question>
					<FAQ.Answer panelId="12">
						<p>
							Regular maintenance, such as cleaning or replacing filters,
							inspecting coolant levels, and checking ductwork, plays a crucial
							role in maintaining your HVAC system&apos;s efficiency. Over time,
							neglected systems can lose efficiency, consume more energy, and
							ultimately fail sooner than well-maintained units. Schedule annual
							inspections with a qualified technician to ensure optimal
							performance and prolong the lifespan of your system.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="13">
						Can you help me replace my HVAC system?
					</FAQ.Question>
					<FAQ.Answer panelId="13">
						<p>
							Absolutely! We’re a tech company bringing a breath of fresh air to
							the HVAC world.{' '}
							<Link
								href="/replace/virtual/"
								className="text-sky-700 hover:text-sky-800 hover:underline"
							>
								Replace your HVAC
							</Link>{' '}
							with us and enjoy a customized solution with upfront pricing. Get
							an honest quote quickly by submitting a little information in just
							few easy steps. Our team will craft a custom solution for your
							home, ensuring you get the trusted help you deserve with fair,
							honest pricing.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
			</FAQ.Block>
		</FAQ>
	);
};

export default LoadCalculatorFAQs;
